import styled from 'styled-components';
import { FlexColumn, FormControls } from '@elementary/common-ui/components';
import { TextField as MuiTextField } from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import { AddTestForm } from 'shared/types';
import { fontSize, fontWeight } from '@elementary/common-ui/style/utils';

const TITLE_FONT_SIZE = '22px';

const TitleField = styled(MuiTextField)<{ $hasError?: boolean }>`
  & .MuiInputBase-input {
    font-size: ${TITLE_FONT_SIZE};
    font-weight: ${fontWeight('semibold')};
    padding: 0;
    color: ${({ theme, $hasError }) =>
      $hasError
        ? theme.palette.status.error.bright
        : theme.palette.content.primary};
  }

  & .MuiInputBase-input::placeholder {
    opacity: 1;
    font-weight: ${fontWeight('semibold')};
    color: ${({ theme, $hasError }) =>
      $hasError
        ? theme.palette.status.error.bright
        : theme.palette.content.secondary};
  }
`;

const DescriptionField = styled(MuiTextField)`
  & .MuiInputBase-input {
    font-size: ${fontSize('base')};
    padding: 0;
    color: ${({ theme }) => theme.palette.content.primary};
  }

  & .MuiInputBase-input::placeholder {
    opacity: 1;
    color: ${({ theme }) => theme.palette.content.secondary};
  }
`;

type Props = {
  methods: UseFormReturn<AddTestForm>;
  titleField?: 'testName' | 'testNameOverride';
  titleValidate?: (value: string | undefined) => true | string;
};

const TestHeader = ({
  methods,
  titleField = 'testName',
  titleValidate,
}: Props) => {
  const { ref: titleRef, ...titleRest } = methods.register(titleField, {
    validate: titleValidate,
  });
  const { ref: descriptionRef, ...descriptionRest } =
    methods.register('description');
  const titleError = methods.formState.errors[titleField]?.message;

  return (
    <FlexColumn sx={{ gap: 0.5 }}>
      <FormControls.InputWrapper>
        <TitleField
          fullWidth
          variant="standard"
          placeholder="Untitled"
          autoComplete="off"
          InputProps={{ disableUnderline: true }}
          inputProps={{ 'aria-label': 'Test name' }}
          inputRef={titleRef}
          {...titleRest}
          error={!!titleError}
          $hasError={!!titleError}
        />
        <FormControls.ErrorMessage>{titleError}</FormControls.ErrorMessage>
      </FormControls.InputWrapper>
      <DescriptionField
        fullWidth
        multiline
        variant="standard"
        placeholder="Add a description…"
        autoComplete="off"
        InputProps={{ disableUnderline: true }}
        inputRef={descriptionRef}
        {...descriptionRest}
      />
    </FlexColumn>
  );
};

export default TestHeader;
